import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from "react";

// First-party consent state persisted in localStorage (NOT a cookie), so an
// un-consented page load sets zero third-party cookies. Third-party scripts
// (Google AdSense = advertising, Microsoft Clarity = analytics) are injected
// only for the categories the visitor allows — see ConsentGatedScripts.
export type ConsentPrefs = {
  analytics: boolean; // Microsoft Clarity (session insights)
  advertising: boolean; // Google AdSense
};

const STORAGE_KEY = "gow-cookie-consent";
const ALL_ON: ConsentPrefs = { analytics: true, advertising: true };
const ALL_OFF: ConsentPrefs = { analytics: false, advertising: false };

type ConsentContextType = {
  prefs: ConsentPrefs;
  decided: boolean;
  hasHydrated: boolean;
  acceptAll: () => void;
  rejectAll: () => void;
  save: (prefs: ConsentPrefs) => void;
  reopen: () => void;
  // Back-compat aliases used elsewhere.
  accept: () => void;
  decline: () => void;
};

const ConsentContext = createContext<ConsentContextType>({
  prefs: ALL_OFF,
  decided: false,
  hasHydrated: false,
  acceptAll: () => {},
  rejectAll: () => {},
  save: () => {},
  reopen: () => {},
  accept: () => {},
  decline: () => {},
});

export const useConsent = () => useContext(ConsentContext);

// Mirrors the attribute the inline script in _document.tsx sets before first
// paint; globals.scss collapses `.ad-slot-reserved` without it. Kept on <html>
// (outside React's tree) for the same reason the theme attribute is.
const setAdsAttribute = (advertising: boolean | null) => {
  try {
    if (advertising) document.documentElement.setAttribute("data-ads", "1");
    else document.documentElement.removeAttribute("data-ads");
  } catch {
    /* no document (SSR) */
  }
};

const updateGoogleConsentMode = (advertising: boolean) => {
  try {
    const w = window as unknown as { gtag?: (...args: unknown[]) => void };
    if (typeof w.gtag === "function") {
      w.gtag("consent", "update", {
        ad_storage: advertising ? "granted" : "denied",
        ad_user_data: advertising ? "granted" : "denied",
        ad_personalization: advertising ? "granted" : "denied",
        analytics_storage: "granted", // GA is first-party, always allowed
      });
    }
  } catch {
    /* gtag not ready yet — default consent (denied) already applied */
  }
};

const parseStored = (raw: string | null): ConsentPrefs | null => {
  if (!raw) return null;
  // Legacy string values from the previous binary banner.
  if (raw === "accepted") return ALL_ON;
  if (raw === "declined") return ALL_OFF;
  try {
    const obj = JSON.parse(raw);
    if (obj && typeof obj === "object") {
      return { analytics: !!obj.analytics, advertising: !!obj.advertising };
    }
  } catch {
    /* ignore malformed value */
  }
  return null;
};

export function ConsentProvider({ children }: { children: ReactNode }) {
  const [prefs, setPrefs] = useState<ConsentPrefs>(ALL_OFF);
  const [decided, setDecided] = useState(false);
  const [hasHydrated, setHasHydrated] = useState(false);

  useEffect(() => {
    let stored: string | null = null;
    try {
      stored = localStorage.getItem(STORAGE_KEY);
    } catch {
      /* storage unavailable (private mode) — stay undecided */
    }
    const parsed = parseStored(stored);
    if (parsed) {
      setPrefs(parsed);
      setDecided(true);
      updateGoogleConsentMode(parsed.advertising);
      setAdsAttribute(parsed.advertising);
    }
    setHasHydrated(true);
  }, []);

  const persist = useCallback((next: ConsentPrefs) => {
    setPrefs(next);
    setDecided(true);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* ignore */
    }
    updateGoogleConsentMode(next.advertising);
    setAdsAttribute(next.advertising);
  }, []);

  const reopen = useCallback(() => {
    setDecided(false);
    setPrefs(ALL_OFF);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    updateGoogleConsentMode(false);
    setAdsAttribute(null);
  }, []);

  return (
    <ConsentContext.Provider
      value={{
        prefs,
        decided,
        hasHydrated,
        acceptAll: () => persist(ALL_ON),
        rejectAll: () => persist(ALL_OFF),
        save: persist,
        reopen,
        accept: () => persist(ALL_ON),
        decline: () => persist(ALL_OFF),
      }}
    >
      {children}
    </ConsentContext.Provider>
  );
}
